import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface Hosting {
  id: string;
  user_id: string;
  name: string;
  panel_url: string;
  panel_login?: string;
  panel_password?: string;
  ftp_login?: string;
  ftp_password?: string;
  dns1?: string;
  dns2?: string;
  ip_address?: string;
  expiry_date?: string;
  created_at: string;
  updated_at: string;
}

export interface NewHosting {
  name: string;
  panel_url: string;
  panel_login?: string;
  panel_password?: string;
  ftp_login?: string;
  ftp_password?: string;
  dns1?: string;
  dns2?: string;
  ip_address?: string;
  expiry_date?: string;
}

export const useHostings = () => {
  const [hostings, setHostings] = useState<Hosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch hostings from database
  const fetchHostings = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('hostings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setHostings(data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas pobierania hostingów';
      setError(errorMessage);
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id, toast]);

  // Create new hosting
  const createHosting = useCallback(async (hostingData: NewHosting): Promise<Hosting | null> => {
    if (!user) {
      toast({
        title: "Błąd",
        description: "Musisz być zalogowany",
        variant: "destructive",
      });
      return null;
    }

    try {
      // Convert empty strings to 'EMPTY' for database
      const processedData = {
        ...hostingData,
        panel_login: hostingData.panel_login || 'EMPTY',
        panel_password: hostingData.panel_password || 'EMPTY',
        ftp_login: hostingData.ftp_login || 'EMPTY',
        ftp_password: hostingData.ftp_password || 'EMPTY',
        dns1: hostingData.dns1 || 'EMPTY',
        dns2: hostingData.dns2 || 'EMPTY',
        ip_address: hostingData.ip_address || 'EMPTY',
        expiry_date: hostingData.expiry_date || 'EMPTY',
      };

      const { data, error } = await supabase
        .from('hostings')
        .insert({
          user_id: user.id,
          ...processedData,
        })
        .select()
        .single();

      if (error) throw error;

      setHostings(prev => [data, ...prev]);
      toast({
        title: "Sukces",
        description: "Hosting został dodany",
      });

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas dodawania hostingu';
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
      return null;
    }
  }, [user, toast]);

  // Create multiple hostings (bulk)
  const createHostingsBulk = useCallback(async (hostingsData: NewHosting[]): Promise<Hosting[]> => {
    if (!user) {
      toast({
        title: "Błąd",
        description: "Musisz być zalogowany",
        variant: "destructive",
      });
      return [];
    }

    try {
      const hostingsWithUser = hostingsData.map(hosting => ({
        user_id: user.id,
        panel_login: hosting.panel_login || 'EMPTY',
        panel_password: hosting.panel_password || 'EMPTY',
        ftp_login: hosting.ftp_login || 'EMPTY',
        ftp_password: hosting.ftp_password || 'EMPTY',
        dns1: hosting.dns1 || 'EMPTY',
        dns2: hosting.dns2 || 'EMPTY',
        ip_address: hosting.ip_address || 'EMPTY',
        expiry_date: hosting.expiry_date || 'EMPTY',
        name: hosting.name,
        panel_url: hosting.panel_url,
      }));

      const { data, error } = await supabase
        .from('hostings')
        .insert(hostingsWithUser)
        .select();

      if (error) throw error;

      setHostings(prev => [...data, ...prev]);
      toast({
        title: "Sukces",
        description: `Dodano ${data.length} hostingów`,
      });

      return data || [];
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas dodawania hostingów';
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
      return [];
    }
  }, [user, toast]);

  // Update hosting
  const updateHosting = useCallback(async (id: string, updates: Partial<NewHosting>): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('hostings')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setHostings(prev => prev.map(hosting =>
        hosting.id === id ? { ...hosting, ...updates } : hosting
      ));

      toast({
        title: "Sukces",
        description: "Hosting został zaktualizowany",
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas aktualizacji hostingu';
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  }, [toast]);

  // Delete hosting
  const deleteHosting = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('hostings')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setHostings(prev => prev.filter(hosting => hosting.id !== id));
      toast({
        title: "Sukces",
        description: "Hosting został usunięty",
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas usuwania hostingu';
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  }, [toast]);

  // Renew hosting expiry date (+1 year)
  const renewHostingExpiry = useCallback(async (id: string): Promise<boolean> => {
    try {
      const hosting = hostings.find(h => h.id === id);
      if (!hosting?.expiry_date) return false;

      const currentDate = new Date(hosting.expiry_date);
      const newExpiryDate = new Date(currentDate);
      newExpiryDate.setFullYear(currentDate.getFullYear() + 1);

      const { error } = await supabase
        .from('hostings')
        .update({
          expiry_date: newExpiryDate.toISOString().split('T')[0]
        })
        .eq('id', id);

      if (error) throw error;

      setHostings(prev => prev.map(hosting =>
        hosting.id === id
          ? { ...hosting, expiry_date: newExpiryDate.toISOString().split('T')[0] }
          : hosting
      ));

      toast({
        title: "Sukces",
        description: "Hosting został odnowiony na następny rok",
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas odnawiania hostingu';
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  }, [hostings, toast]);

  // Set up real-time subscription
  useEffect(() => {
    if (!user) return;

    const subscription = supabase
      .channel('hostings_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'hostings',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setHostings(prev => {
            const exists = prev.some(h => h.id === payload.new.id);
            if (!exists) {
              return [payload.new as Hosting, ...prev];
            }
            return prev;
          });
        } else if (payload.eventType === 'UPDATE') {
          setHostings(prev => prev.map(h =>
            h.id === payload.new.id ? payload.new as Hosting : h
          ));
        } else if (payload.eventType === 'DELETE') {
          setHostings(prev => prev.filter(h => h.id !== payload.old.id));
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user?.id]);

  // Initial fetch
  useEffect(() => {
    if (user?.id) {
      fetchHostings();
    }
  }, [user?.id, fetchHostings]);

  return {
    hostings,
    loading,
    error,
    createHosting,
    createHostingsBulk,
    updateHosting,
    deleteHosting,
    renewHostingExpiry,
    refreshHostings: fetchHostings,
  };
};
